import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, CircularProgress, IconButton, FormControlLabel, Checkbox } from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { doc } from 'firebase/firestore';
import { db, SECRETS_ENV } from "../../../Config/firebase.ts";
import { toast } from 'react-toastify';
import { getFutureCycles } from "../utilityFunctions.ts";

import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN

interface FormData {
    cycle: string;
    selectedSubscriptions: string[];
}

interface Subscription {
    id: string;
    Name: string;
    showMeInList: boolean;
    activeSubscribersCount: number;
    subscriptionType: string;
}

export default function BulkRemindersModal() {
    // Modal Controllers
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const open = useAppSelector((state: RootState) => state.ModalSlice.BulkRemindersModalOpen);
    const serviceProviderId = useAppSelector(selectEffectiveUID); 
    const subscriptions = useAppSelector((state) => state.subscriptionTableSlice.data);

    // Component States
    const [formData, setFormData] = useState<FormData>({
        cycle: "",
        selectedSubscriptions: []
    });

    const [isLoading, setIsLoading] = useState(false);
    const [availableCycles, setAvailableCycles] = useState<string[]>([]);
    const { axiosPostRequest } = useBackend("");
    const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);

    // UseEffect to initialize available cycles
    useEffect(() => {
        setIsSubscriptionsLoading(true);
        // Get future cycles for dropdown
        const cycles = getFutureCycles().slice(0, 2);
        setAvailableCycles(cycles);

        // Get all eligible subscription IDs
        const eligibleSubscriptionIds = subscriptions?.filter(subscription => subscription.showMeInList === true)
            .map(subscription => subscription.id) ?? [];

        // Initialize form with first cycle and all eligible subscriptions
        setFormData({
            cycle: cycles.length > 0 ? cycles[0] : "",
            selectedSubscriptions: eligibleSubscriptionIds
        });

        setIsSubscriptionsLoading(false);
    }, [open, subscriptions]);

    // Create an array of subscriptions with references and check eligibility
    const subscriptionsWithReference = (subscriptions ?? [] as Subscription[])
        .filter(subscription => subscription.showMeInList === true)
        .map(subscription => ({
            ...subscription,
            reference: doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscription.id}`),
            isEligible: true
        }));

    // Handler Functions
    const handleClose = () => {
        dispatch(closeModal("BulkRemindersModalOpen"));
        resetForm();
    };

    const handleSubscriptionChange = (subscriptionId: string, checked: boolean) => {
        setFormData(prev => {
            const newSubscriptions = checked
                ? [...prev.selectedSubscriptions, subscriptionId]
                : prev.selectedSubscriptions.filter(id => id !== subscriptionId);

            setFormData(prev => ({ ...prev, selectedSubscriptions: newSubscriptions }));

            return {
                ...prev,
                selectedSubscriptions: newSubscriptions
            };
        });
    };

    // Reset Form Function
    const resetForm = () => {
        setFormData({
            cycle: availableCycles.length > 0 ? availableCycles[0] : "",
            selectedSubscriptions: []
        });
    };

    // Validation Function
    const validateForm = (): boolean => {
        if (formData.cycle === "") {
            toast.error("Please select an invoice cycle");
            return false;
        }

        if (formData.selectedSubscriptions.length === 0) {
            toast.error("Please select at least one subscription");
            return false;
        }

        return true;
    };

    // Submit Function
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        dispatch(toggleLoading(true));

        try {
            const requestData = {
                ProviderID: serviceProviderId,
                SubscriptionIDs: formData.selectedSubscriptions,
                secretToken: secretCloudTaskToken
            };

            const response: any = await axiosPostRequest(
                `${SECRETS_ENV.API_END_POINT}/api/BulkUsabilityFeatures/reminders`,
                requestData
            );


            if (response.data?.message) {
                toast.success(response.data.message);
                handleClose();
                navigate('/bulkrequests');
            } else {
                toast.error("Failed to send reminders, please try again");
            }

        } catch (error) {
            console.error("Error sending reminders:", error);
            toast.error("Failed to send reminders");
        } finally {
            setIsLoading(false);
            dispatch(toggleLoading(false));
        }
    };

    // Add these handler functions after the existing handler functions
    const handleSelectAll = () => {
        const allSubscriptionIds = subscriptionsWithReference.map(sub => sub.id);
        setFormData(prev => ({
            ...prev,
            selectedSubscriptions: allSubscriptionIds
        }));
    };

    const handleDeselectAll = () => {
        setFormData(prev => ({
            ...prev,
            selectedSubscriptions: []
        }));
    };

    return (
        <>
        <Dialog
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="md"
            open={open}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
                <div className="w-[550px]">
                    <div className="header pb-[20px]">
                        <p className="text-cf_heading font-[600]">Send Reminders</p>
                        <p className="text-cf_small font-[400]">Select the subscriptions you want to send reminders to</p>
                    </div>

                    <div className="absolute right-4 top-4">
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <Box className="space-y-4">

                        <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-sm font-medium">
                                    <span className="font-semibold">Select Subscriptions</span>
                                    <span className="font-[400]"> - ({formData.selectedSubscriptions.length} selected)</span>
                                </p>
                                <button
                                    onClick={formData.selectedSubscriptions.length === subscriptionsWithReference.length ? handleDeselectAll : handleSelectAll}
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                    {formData.selectedSubscriptions.length === subscriptionsWithReference.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                                {isSubscriptionsLoading ? (
                                    <div className="flex justify-center items-center h-20">
                                        <CircularProgress size={24} />
                                        Loading...
                                    </div>
                                ) : (
                                    subscriptionsWithReference.map((subscription, index) => (
                                        <div
                                            key={index}
                                            className={`p-2 border-b last:border-b-0 `}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.selectedSubscriptions.some(
                                                            id => id === subscription.id
                                                        )}
                                                        onChange={(e) => handleSubscriptionChange(
                                                            subscription.id,
                                                            e.target.checked
                                                        )}

                                                    />
                                                }
                                                label={
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{subscription.Name}</span>
                                                        <span className="text-xs text-gray-500">
                                                            {`${subscription.activeSubscribersCount} Students`} • {subscription.subscriptionType}
                                                        </span>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="mt-2 bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                            <p className="font-medium">Note:</p>
                            <p>All unpaid students will be reminded to pay via whatsapp.</p>
                        </div>

                        <div className="flex gap-2 pt-4">
                            <button
                                disabled={isLoading}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClose();
                                }}
                                className="w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                            >
                                {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                                <p className="ml-2">{isLoading ? "Processing" : "Confirm"}</p>
                            </button>
                        </div>
                    </Box>
                </div>
            </DialogContent>
        </Dialog>
        
        </>
    );
} 